import { useAccessControl, useStaffOrdersSubscription } from '@billx/api';
import { Box, Stack } from '@mui/material';
import { SendToBackIcon } from 'lucide-react';
import React from 'react';
import { use100vh } from 'react-div-100vh';
import { useNavigate } from 'react-router-dom';

import {
  AppSidebar,
  BrandNameWithLogo,
  SidebarProvider,
  ThemeToggle,
  UtilityIconButton,
} from '@/components/common';
import { useStaffNavItems } from '@/hooks';

import { ProfileSection } from './ProfileSection';

interface StaffLayoutProps {
  children: React.ReactNode;
}

export const StaffLayout: React.FC<StaffLayoutProps> = ({ children }) => {
  const height = use100vh();
  const navItems = useStaffNavItems();
  const navigate = useNavigate();
  const { canAccessPage } = useAccessControl();
  useStaffOrdersSubscription();

  return (
    <SidebarProvider
      initialState="collapsed"
      isCollapsible={false}
      variant="collapsible"
    >
      <Box sx={{ display: 'flex', height }}>
        <AppSidebar
          navItems={navItems}
          header={(isCollapsed) => {
            return (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={isCollapsed ? 'center' : 'space-between'}
                sx={{
                  p: 2,
                  minHeight: 64,
                }}
              >
                <BrandNameWithLogo showLogoOnly={isCollapsed} />
              </Stack>
            );
          }}
          footer={() => {
            return (
              <Stack
                sx={{ p: 1, borderRadius: 1, overflow: 'hidden' }}
                spacing={1}
              >
                <ThemeToggle tooltip={{ placement: 'right' }} />
                {canAccessPage('dashboard') && (
                  <UtilityIconButton
                    tooltip={{ title: 'Dashboard', placement: 'right' }}
                    onClick={() => navigate('/dashboard')}
                  >
                    <SendToBackIcon size={16} />
                  </UtilityIconButton>
                )}
                <ProfileSection />
              </Stack>
            );
          }}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            mt: 0,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'scroll',
          }}
        >
          {children}
        </Box>
      </Box>
    </SidebarProvider>
  );
};
